import { SectionShell } from "@/components/design-system/SectionShell";
import { CompositionChart, TimeseriesChart } from "@/components/ui/chart";

const SAMPLE_DATA = [
  { label: "Jan", "Minimarket": 4200, "Hyper-Super": 3100, "H&B": 1800 },
  { label: "Feb", "Minimarket": 4600, "Hyper-Super": 2950, "H&B": 2050 },
  { label: "Mar", "Minimarket": 5100, "Hyper-Super": 3400, "H&B": 1900 },
  { label: "Apr", "Minimarket": 4800, "Hyper-Super": 3650, "H&B": 2300 },
  { label: "Mei", "Minimarket": 5400, "Hyper-Super": 3520, "H&B": 2480 },
  { label: "Jun", "Minimarket": 5950, "Hyper-Super": 3800, "H&B": 2610 },
];

const SAMPLE_SERIES = [
  { key: "Minimarket", name: "Minimarket" },
  { key: "Hyper-Super", name: "Hyper-Super" },
  { key: "H&B", name: "H&B" },
];

const SAMPLE_COMPOSITION = [
  { label: "Minimarket", value: 5950, share: 0.46 },
  { label: "Hyper-Super", value: 3800, share: 0.29 },
  { label: "H&B", value: 2610, share: 0.2 },
  { label: "General Trade", value: 640, share: 0.05 },
];

const code = `import { TimeseriesChart } from "@/components/ui/chart";

const data = [
  { label: "Jan", Minimarket: 4200, "Hyper-Super": 3100 },
  { label: "Feb", Minimarket: 4600, "Hyper-Super": 2950 },
  // ...one row per x-axis point
];

const series = [
  { key: "Minimarket", name: "Minimarket" },
  { key: "Hyper-Super", name: "Hyper-Super" },
];

<TimeseriesChart
  data={data}
  series={series}
  valueFormatter={(v) => v.toLocaleString("id-ID")}
  height={360}
/>`;

export function ChartSection() {
  return (
    <SectionShell
      id="chart"
      title="Chart"
      description={
        <>
          An SSR-safe, multi-series line chart for monthly timeseries data, built
          on <code>recharts</code> and themed entirely with design-system tokens
          (it re-colors automatically in dark mode). It renders a stable
          placeholder during SSR and the first paint, then mounts the live chart
          after hydration — never reference <code>window</code>/<code>document</code>
          in code paths that feed it during render.
        </>
      }
      whenToUse={
        <ul>
          <li>Monthly / periodic value trends with one or more series.</li>
          <li>Comparing a breakdown over time (per channel, per brand, etc.).</li>
          <li>Dashboards where the data shape is <code>{`{ label, ...seriesKeys }`}</code>.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>Tabular detail that needs exact numbers — use a plain table (see Listings).</li>
          <li>A single KPI value — use a stat/badge, not a chart.</li>
        </ul>
      }
      preview={
        <div className="w-full space-y-8">
          <TimeseriesChart
            data={SAMPLE_DATA}
            series={SAMPLE_SERIES}
            valueFormatter={(v) => v.toLocaleString("id-ID")}
            height={320}
          />
          <div>
            <p className="mb-3 text-xs font-medium text-ink-muted">
              CompositionChart — parts-of-whole (Share of Wallet) for one period
            </p>
            <CompositionChart
              data={SAMPLE_COMPOSITION}
              valueFormatter={(v) => v.toLocaleString("id-ID")}
              height={220}
            />
          </div>
        </div>
      }
      code={code}
      options={
        <ul className="mt-1 list-disc pl-5 [&_li]:mt-0.5">
          <li><code>series</code> — ordered list of <code>{`{ key, name }`}</code>; colors are assigned by index from the token palette.</li>
          <li><code>valueFormatter</code> — formats y-axis ticks and tooltip values (e.g. compact IDR).</li>
          <li><code>xKey</code> — row key for the x-axis category (default <code>"label"</code>).</li>
          <li><code>height</code>, <code>emptyMessage</code> — fixed height and the no-data message.</li>
          <li><code>CompositionChart</code> — donut variant for single-period composition; takes <code>{`{ label, value, share? }`}</code> slices and renders a center total + share legend.</li>
        </ul>
      }
    />
  );
}
